import { useFormik } from 'formik';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { useTranslation } from 'next-i18next';
import { i18n } from 'next-i18next.config';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LayoutForm from 'src/components/layout/form/form';
import { LayoutType } from 'src/components/layout/layout';
import Box, { HeaderTheme, HeadingElement } from 'src/components/ui-kit/box/box';
import { ButtonSize, ButtonVariant } from 'src/components/ui-kit/button/button';
import ButtonBlock from 'src/components/ui-kit/button/__block';
import FormTextInput from 'src/components/ui-kit/form/_text-input';
import { configuration } from 'src/configurations/configuration';
import { User } from 'src/entities/user';
import { LoginForm } from 'src/forms/login';
import { wrapper } from 'src/store';
import { AuthActions, AuthSelectors } from 'src/store/auth';
import styles from './sign-in.module.scss';
import Head from 'next/head';
import Icon, { IconTheme, IconVariant } from 'src/components/ui-kit/icon/icon';

const SignInPage = (): JSX.Element => {
  const { t } = useTranslation(['components:pages:sign-in']);

  const initialValues = new LoginForm();
  const validationSchema = LoginForm.getValidationSchema(t);

  const router = useRouter();
  const dispatch = useDispatch();

  const isAuthenticating = useSelector(AuthSelectors.isAuthenticating);
  const isAuthenticated = useSelector(AuthSelectors.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated) {
      router.push('/mineral-listings');
    }
  }, [isAuthenticated]);

  const handleSubmit = (values: LoginForm): void => {
    dispatch(AuthActions.authorize({ user: new User(values) }));
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    validationSchema
  });

  return (
    <>
      <Head>
        <title>US Mineral Exchange | Login</title>
      </Head>
      <LayoutForm className={styles.signin}>
        <Box
          heading={t('TEXT_LOGIN')}
          headingTheme={HeaderTheme.DARK}
          headingElement={HeadingElement.H1}
          className={styles.signin_box}
        >
          <form
            onSubmit={formik.handleSubmit}
            className={styles.signin_form}>
            <p className={styles.signin_description}>
              {t('TEXT_ASTERISK_NOTE_FIRST_PART')}
              <span className={styles.signin_asterisk}> * </span>
              {t('TEXT_ASTERISK_NOTE_SECOND_PART')}
            </p>
            <FormTextInput
              name='userNameOrEmail'
              formik={formik}
              isBordered
              isAsteriskShown
              placeholder={t('TEXT_USER_NAME_OR_EMAIL')}
              className={styles.signin_field}
            />
            <FormTextInput
              name='password'
              type='password'
              formik={formik}
              isBordered
              isAsteriskShown
              placeholder={t('TEXT_PASSWORD')}
              className={styles.signin_field}
            />
            <ButtonBlock
              type='submit'
              variant={ButtonVariant.SECONDARY}
              isLoading={isAuthenticating}
              size={ButtonSize.LARGE}
            >
              {t('BUTTON_LOGIN')}
              {!isAuthenticating && (
                <span className={styles.signin_icon}>
                  <Icon
                    variant={IconVariant.NEXT}
                    theme={IconTheme.WHITE} />
                </span>
              )}
            </ButtonBlock>
          </form>
        </Box>
      </LayoutForm>
    </>
  );
};

SignInPage.layout = LayoutType.HOME;

export default SignInPage;

export const getServerSideProps: GetServerSideProps = wrapper.getServerSideProps(
  () => async ({ locale, req }: GetServerSidePropsContext) => {
    const cookies = req.cookies;
    const isAuthenticated = cookies[configuration.cookies.keys.isAuthenticated] === 'true';

    if (isAuthenticated) {
      return {
        redirect: {
          permanent: false,
          destination: '/profile'
        }
      };
    }

    return {
      props: {
        ...(await serverSideTranslations(locale ?? i18n.defaultLocale, [
          'common',
          'components:layout',
          'components:pages:sign-in'
        ]))
      }
    };
  }
);
